import { HANDLED_BACKED_ERRORS } from '~/modules/settings/constants/handledBackendErrors';
import PolyllmSubscriptionModal from '~/projects/polyllm-chat/components/SubscriptionModal';
import {
  paymentAgreementPolyllmDefault,
  paymentAgreementPolyllmUs,
} from '~/projects/polyllm-chat/config/agreementCopy';
import { getPolyllmUrlLocale } from '~/projects/polyllm-chat/i18n/localePath';
import { shouldShowPolyllmRestoreModal } from '~/projects/polyllm-chat/utils/shouldShowPolyllmRestoreModal';
import { ProjectRuntimeBehavior } from '~/projects/types';

/**
 * Backend error reasons that PolyLLM-Chat still routes through the legacy
 * `ChatPaywallModal` upgrade paywall instead of the shared
 * `useHandleSpecialBackendErrors` modal switch. The NO_ACTIVE_SUBSCRIPTION
 * family (EXPIRED / CANCELED / NOT_ACTIVE) was migrated to the DI
 * `SubscriptionModal` slot in JD-2633 and is intentionally excluded here.
 * This set shrinks as more flows move to ChatWithAI-styled modals and is
 * fully removed by Phase-1 PolyLLM decoupling.
 */
const POLYLLM_UPGRADE_PAYWALL_REASONS: ReadonlySet<HANDLED_BACKED_ERRORS> =
  new Set([
    HANDLED_BACKED_ERRORS.SUBSCRIPTION_IS_IN_REDEMPTION,
    HANDLED_BACKED_ERRORS.SUBSCRIPTION_PAYMENT_IS_PROCESS,
    HANDLED_BACKED_ERRORS.SUBSCRIPTION_REDEMPTION_PAYMENT_FAILED,
    HANDLED_BACKED_ERRORS.SUBSCRIPTION_IS_ON_PAUSE,
    HANDLED_BACKED_ERRORS.FREE_TIER_DAILY_LIMIT_EXCEEDED,
  ]);
const runtimeBehavior: ProjectRuntimeBehavior = {
  title: 'ChatWithAI',
  fetchSubscriptionEndpoint: false,
  createFunnelUserBeforeSignUp: true,
  fetchTrustpilotInviteUrl: false,
  subscriptionFromProfileOnly: true,
  showSubscriptionUsageBlocks: false,
  withOnboardingMiddleware: false,
  settingsMainLayoutNoSidebar: true,
  loadDesktopSidebarThemeToggle: true,
  chatVendorDefaultIsGpt51: true,
  chatVendorSelectorAvailableWithoutFeatureFlag: true,
  chatUsesGptShellLayout: true,
  chatAnonymousSessionsEnabled: true,
  chatUsesChatwithaiEndpoints: true,
  showFrozenSubscriptionBlock: false,
  showHomeRetentionPopover: false,
  showTrustpilotMentions: false,
  openTrustpilotOnChatLike: false,
  shouldShowUpgradePaywallOnHandledBackendError: (errorMessage) =>
    POLYLLM_UPGRADE_PAYWALL_REASONS.has(errorMessage as HANDLED_BACKED_ERRORS),
  shouldShowUpgradePaywallForInactiveSubscriptionStatus: (status) =>
    !shouldShowPolyllmRestoreModal(status),
  SubscriptionModal: PolyllmSubscriptionModal,
  signUpUsesDeviceIdHeader: true,
  showNoActiveSubscriptionModal: false,
  usesPolyllmRedemptionCheckout: true,
  enforcesFrontendChatUsageLimits: true,
  deleteAccountAvailableForAllUsers: true,
  getRouterBasename: () => {
    const locale = getPolyllmUrlLocale(window.location.pathname);
    return locale ? `/${locale}` : undefined;
  },
  paymentConfigByRegion: {
    US: {
      getCompanyAddress: paymentAgreementPolyllmUs,
    },
    default: {
      getCompanyAddress: paymentAgreementPolyllmDefault,
    },
  },
  personalInformationLegalLinksByRegion: {
    default: [
      {
        label: (t) => t('general:terms_of_service'),
        href: '/legal/terms-of-use',
      },
      {
        label: (t) => t('general:privacy_policy'),
        href: '/legal/privacy-policy',
      },
      {
        label: (t) => t('general:subscription_terms'),
        href: '/legal/subscription-terms',
      },
    ],
  },
};
export default runtimeBehavior;
